import React from "react";
import { Helmet } from "react-helmet-async";
import "../styles/HomeComponents/AboutUs.css";

// import your Strawket-style illustrations here
import heroIllustration from "../assets/Images/Group 228.webp";
import activitiesIllustration from "../assets/Images/chess.png";

const AboutUs = () => {
  return (
    <div className="about-page">

      <Helmet>
         <title>About Strawket | Our Story, Mission & Purpose</title>
         <meta 
          name="description" 
          content="Discover Strawket's journey — from a shared vision to a full academy operating system that connects passionate educators with curious learners." 
        />
        <meta 
          name="keywords" 
          content="About Strawket, Strawket story, Strawket mission, edtech platform, academy operating system, educators and learners, student platform" 
        />
        <link rel="canonical" href="https://www.strawket.com/about" />

        {/* Open Graph (Facebook, LinkedIn, etc.) */}
        <meta property="og:title" content="About Strawket | A Platform Built on Passion for Learning" />
        <meta 
          property="og:description" 
          content="Learn how Strawket was built to unburden educators, empower learners, and simplify running an academy. Real pain points. Real solutions." 
        />
        <meta property="og:url" content="https://www.strawket.com/about" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.strawket.com/assets/images/strawket-about-preview.png" />

        {/* Twitter Card */}
        <meta name="twitter:title" content="About Strawket | Built for Educators & Learners" />
        <meta 
          name="twitter:description" 
          content="Discover the story behind Strawket — a platform that simplifies education by connecting passionate educators with learners worldwide." 
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://www.strawket.com/assets/images/strawket-about-preview.png" />
      </Helmet>

      {/* HERO */}
      <section className="about-hero">
        <div className="about-hero-inner">
          <div className="about-hero-text">
            <h1>Why is it so hard to follow your passion?</h1>
            <p>
              Strawket began with a simple question we kept hearing over and over.
            </p>
          </div>

          <div className="about-hero-visual">
            <img src={heroIllustration} alt="Learner and educator conversation" />
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="about-story">
        <div className="about-container">
          <p>
            Back when we were building our own academy, we spent countless hours
            speaking with learners, parents, and educators. The conversations
            were eye-opening.
          </p>

          <p>
            Educators were deeply passionate about teaching — but overwhelmed by
            everything else… scheduling, reminders, attendance, reports,
            payments, marketing.
          </p>

          <p>
            All the admin work that was supposed to support teaching was actually
            standing in the way of it.
          </p>

          <p>
            Many incredible educators told us they hadn’t even started their
            academy because they were intimidated by the non-teaching parts.
            That felt like a huge loss — not just for them, but for learners who
            would have thrived under their guidance.
          </p>

          <p>
            Parents, on the other hand, kept asking,
            <span className="highlight">
              {" "}
              “Do you offer more? What else can my child learn?”
            </span>
          </p>

          <p>
            They wanted a simple way to find the right teacher — someone who
            truly understood their child’s interests. And they wanted clarity
            about what was being taught, without the usual confusion.
          </p>

          <p className="pattern">
            As we listened, a pattern emerged:
            <br />
            <strong>
              Learners wanted direction. Educators wanted freedom. And both
              needed a bridge to find each other.
            </strong>
          </p>
        </div>
      </section>

      {/* ACTIVITIES */}
      <section className="about-activities">
        <div className="about-container activities-inner">
          <div className="activities-visual">
            <img src={activitiesIllustration} alt="Different learning activities" />
          </div>

          <div className="activities-text">
            <p>
              That’s when the idea for Strawket became clear.
            </p>

            <p>
              We didn’t want to build yet another academy.
            </p>

            <p>
              We wanted to build the platform that empowers passionate educators
              everywhere — giving them the tools to run an academy with ease,
              and helping learners discover the right teacher for their passion.
            </p>

            <p className="closing">
              So we turned our conversations and early learnings into Strawket —
              <strong> a simple operating system for an academy.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="about-mission">
        <div className="about-container">
          <h2>Our Mission</h2>

          <p className="mission-lead">
            At Strawket, our mission is simple:
            <strong> Make it easy for anyone to follow their passion.</strong>
          </p>

          <p>
            We give educators a simple, powerful way to run an academy — so they
            can focus on what they truly love: teaching.
          </p>

          <p>
            And we help learners find the right teacher effortlessly, connecting
            them with mentors who can spark curiosity, confidence, and lifelong
            passion.
          </p>

          <p className="mission-closing">
            Strawket exists to remove the barriers on both sides — unburdening
            educators, empowering learners, and connecting curious learners
            with passionate educators.
          </p>
        </div>
      </section>

    </div>
  );
};

export default AboutUs;

